import { catalog, plural } from '@nolens/i18n';

export const workspaceText = catalog(
  {
    spaceNotFound: 'Space not found',
    spaceNotFoundHint: "It doesn't exist or you're no longer a member.",
    onlyYou: 'Only you see these guides.',
    teamLine: (members: number, role: string) =>
      `${members} ${plural('en', members, { one: 'member', other: 'members' })} · you're ${role.toLowerCase()}`,
    savings: 'Savings',
    brand: 'Brand',
    members: 'Members',
    searchGuides: 'Search guides',
    searchPlaceholder: 'Search guides…',
    noGuides: 'No guides yet',
    noGuidesHint:
      'Record a guide with the Nolens Guides extension and click “Finish”: it shows up here.',
    connectExtension: 'Connect the extension',
    installExtension: 'Install the extension',
    noGuidesInstall:
      'Install the Nolens Guides extension in Chrome to record your first guide: do the process once and it becomes a step-by-step guide.',
    noGuidesLink:
      'The extension is installed but not connected to this account. Connect it to record your first guide.',
    noGuidesReady:
      'Click the Nolens Guides icon in the Chrome toolbar and “Start recording”. When you click “Finish”, the guide shows up here.',
    noGuidesTeam:
      'Guides you record go to your personal space first. To bring one here, open it and choose this team under “Space”.',
    noGuidesViewer: 'When someone in the team adds guides, they show up here.',
    noMatch: (q: string) => `No guides with “${q}”.`,
    untitled: 'Untitled',
    steps: (n: number) => `${n} ${plural('en', n, { one: 'step', other: 'steps' })}`,
    publicLink: 'Public link',
    newTeam: 'New team',
    newTeamHint: 'A team’s guides are visible to all members. You can invite people next.',
    teamName: 'Team name',
    teamNamePlaceholder: 'E.g. Customer support',
    createTeam: 'Create team',
    teamPlan: 'Pro Team plan',
    teamPlanHint: (price: string, editors: number) =>
      `On the Free plan, Nolens Guides is just for you. To work as a team, subscribe to Pro Team: from ${price}/month + VAT for ${editors} editors, with unlimited readers for free.`,
    teamPlanNext:
      'Name the team, then choose how to pay. The team becomes active when the subscription starts.',
    comparePlans: 'Compare plans',
    continueToSubscribe: 'Continue to subscription',
    seePlans: 'See plans',
    trialUntil: (date: string) => ` · trial until ${date}`,
    guidesUsage: (used: number, max: number) => `${used} of ${max} guides`,
    storageUsage: (mb: string, gb: string) => `${mb} MB of ${gb} GB in screenshots`,
    // Biblioteca
    view: 'View',
    grid: 'Grid',
    list: 'List',
    colGuide: 'Guide',
    colSpace: 'Space',
    colAuthor: 'Recorded by',
    colSteps: 'Steps',
    colUpdated: 'Updated',
    updated: (when: string) => `Updated ${when}`,
    byAuthor: (who: string) => `by ${who}`,
    personalSpace: 'Personal space',
    team: 'Team',
    // Os meus guias
    myGuides: 'My guides',
    myGuidesHint: 'The guides you recorded, in all your spaces.',
    // Início
    home: 'Home',
    welcome: 'Welcome to Nolens Guides',
    welcomeBack: 'Welcome back',
    homeHint: 'Record a process once and turn it into a step-by-step guide.',
    recent: 'Recent',
    seeMyGuides: 'See my guides',
    yourNumbers: 'Your numbers',
    guidesRecorded: 'Guides recorded',
    reads30: 'Reads in 30 days',
    readsAllTime: (n: number) => `${n} all time`,
    timeSaved: 'Time saved',
    estimate: 'estimate',
    stepsDocumented: (n: number) =>
      `${n} ${plural('en', n, { one: 'step', other: 'steps' })} documented`,
  },
  {
    'pt-PT': {
      spaceNotFound: 'Espaço não encontrado',
      spaceNotFoundHint: 'Não existe ou já não é membro.',
      onlyYou: 'Só você vê estes guias.',
      teamLine: (members, role) =>
        `${members} ${plural('pt-PT', members, { one: 'membro', other: 'membros' })} · é ${role.toLowerCase()}`,
      savings: 'Poupança',
      brand: 'Marca',
      members: 'Membros',
      searchGuides: 'Procurar guias',
      searchPlaceholder: 'Procurar guias…',
      noGuides: 'Ainda não há guias',
      noGuidesHint:
        'Grave um guia com a extensão Nolens Guides e carregue em «Concluir»: aparece aqui.',
      connectExtension: 'Ligar a extensão',
      installExtension: 'Instalar a extensão',
      noGuidesInstall:
        'Instale a extensão Nolens Guides no Chrome para gravar o seu primeiro guia: faça o processo uma vez e ele fica um guia passo a passo.',
      noGuidesLink:
        'A extensão está instalada, mas não está ligada a esta conta. Ligue-a para gravar o seu primeiro guia.',
      noGuidesReady:
        'Clique no ícone Nolens Guides na barra do Chrome e em «Começar a gravar». Quando carregar em «Concluir», o guia aparece aqui.',
      noGuidesTeam:
        'Os guias que grava ficam primeiro no seu espaço pessoal. Para trazer um para aqui, abra-o e escolha esta equipa em «Espaço».',
      noGuidesViewer: 'Quando alguém da equipa juntar guias, aparecem aqui.',
      noMatch: (q) => `Nenhum guia com «${q}».`,
      untitled: 'Sem título',
      steps: (n) => `${n} ${plural('pt-PT', n, { one: 'passo', other: 'passos' })}`,
      publicLink: 'Ligação pública',
      newTeam: 'Nova equipa',
      newTeamHint:
        'Os guias de uma equipa ficam visíveis para todos os membros. Pode convidar pessoas a seguir.',
      teamName: 'Nome da equipa',
      teamNamePlaceholder: 'Ex.: Apoio ao cliente',
      createTeam: 'Criar equipa',
      teamPlan: 'Plano Pro Equipa',
      teamPlanHint: (price, editors) =>
        `No plano Grátis, o Nolens Guides é só para si. Para trabalhar em equipa, subscreva o Pro Equipa: a partir de ${price}/mês + IVA para ${editors} editores, com leitores ilimitados e grátis.`,
      teamPlanNext:
        'Dê um nome à equipa e escolha a seguir como pagar. A equipa fica ativa quando a subscrição começar.',
      comparePlans: 'Comparar os planos',
      continueToSubscribe: 'Continuar para a subscrição',
      seePlans: 'Ver planos',
      trialUntil: (date) => ` · teste até ${date}`,
      guidesUsage: (used, max) => `${used} de ${max} guias`,
      storageUsage: (mb, gb) => `${mb} MB de ${gb} GB em capturas`,
      view: 'Vista',
      grid: 'Grelha',
      list: 'Lista',
      colGuide: 'Guia',
      colSpace: 'Espaço',
      colAuthor: 'Gravado por',
      colSteps: 'Passos',
      colUpdated: 'Alterado',
      updated: (when) => `Alterado ${when}`,
      byAuthor: (who) => `por ${who}`,
      personalSpace: 'Espaço pessoal',
      team: 'Equipa',
      myGuides: 'Os meus guias',
      myGuidesHint: 'Os guias que gravou, em todos os seus espaços.',
      home: 'Início',
      welcome: 'Boas-vindas ao Nolens Guides',
      welcomeBack: 'Olá de novo',
      homeHint: 'Grave um processo uma vez e transforme-o num guia passo a passo.',
      recent: 'Recentes',
      seeMyGuides: 'Ver os meus guias',
      yourNumbers: 'Os seus números',
      guidesRecorded: 'Guias gravados',
      reads30: 'Leituras em 30 dias',
      readsAllTime: (n) => `${n} desde o início`,
      timeSaved: 'Tempo poupado',
      estimate: 'estimativa',
      stepsDocumented: (n) =>
        `${n} ${plural('pt-PT', n, { one: 'passo documentado', other: 'passos documentados' })}`,
    },
  },
);
